import { formatNumber, formatRelativeTime } from "@excellent/ui";
import { href } from "../../nav";
import {
  ActionButton,
  ButtonRow,
  Callout,
  DataTable,
  Mono,
  Section,
  Stat,
  StatGrid,
  ToneBadge,
  Verdict,
  type DataTableColumn,
} from "../../ui";
import { PageFrame, type VerificationPageProps } from "../page-frame";
import {
  ARTIFACTS,
  CONTROLS,
  FINDINGS,
  METRIC_TONE,
  METRICS,
  RECEIPTS,
  RUNS,
  CANDIDATES,
  type Candidate,
  type Finding,
  type Metric,
} from "../../../prototypes/verification/data";

export const OUTCOMES_OBSERVE_ACTION = "outcomes.observe";
export const OUTCOMES_OPEN_RECEIPT_ACTION = "outcomes.open-receipt";
export const AUTONOMY_SIMULATE_ACTION = "autonomy.simulate";
export const AUTONOMY_OPEN_WORKFLOW_ACTION = "autonomy.open-workflow";
export const ANALYTICS_EXPORT_ACTION = "analytics.export";
export const ANALYTICS_OPEN_DRILLDOWN_ACTION = "analytics.open-drilldown";

const simulateDisabledReason =
  "Simulation requires a canonical verification simulation contract before it can affect policy.";
const exportDisabledReason =
  "Analytics export requires a server-authored aggregate contract; browser-computed rollups are not evidence packs.";

const outcomeRows = RUNS.map((run) => {
  const receipt = RECEIPTS.find((row) => row.runId === run.id) ?? null;
  return {
    id: run.id,
    label: run.label,
    aggregate: run.aggregate,
    status: run.status,
    completedAt: run.completedAt,
    receiptId: receipt?.id ?? run.receiptId,
    evidenceCount: run.evidenceArtifactIds.length,
    controlCount: run.controlIds.length,
  };
});

const autonomyColumns = (onNavigate: (to: string) => void): DataTableColumn<Candidate>[] => [
  {
    key: "workflow",
    header: "Workflow",
    cellClassName: "whitespace-normal",
    render: (row) => (
      <div className="flex flex-col gap-0.5">
        <span className="font-medium text-foreground">{row.name}</span>
        <span className="text-muted-foreground">{row.actor}</span>
      </div>
    ),
  },
  {
    key: "oracle",
    header: "Independent oracle",
    cellClassName: "whitespace-normal",
    render: (row) => row.oracle ?? row.rejectionReason ?? "No oracle named",
  },
  {
    key: "consequence",
    header: "Consequence",
    render: (row) => (
      <ToneBadge tone={row.consequence === "critical" ? "fail" : "unknown"}>
        {row.consequence}
      </ToneBadge>
    ),
  },
  {
    key: "status",
    header: "Admission",
    render: (row) => (
      <ToneBadge
        tone={row.status === "admitted" ? "pass" : row.status === "reject" ? "fail" : "unknown"}
      >
        {row.status}
      </ToneBadge>
    ),
  },
  {
    key: "action",
    header: "Action",
    render: (row) => (
      <ButtonRow>
        <ActionButton
          actionId={AUTONOMY_OPEN_WORKFLOW_ACTION}
          data-action-id={AUTONOMY_OPEN_WORKFLOW_ACTION}
          data-workflow-id={row.id}
          onClick={() => onNavigate(href("workflows", row.id))}
        >
          Open
        </ActionButton>
      </ButtonRow>
    ),
  },
];

const findingColumns = (onNavigate: (to: string) => void): DataTableColumn<Finding>[] => [
  { key: "ref", header: "Finding", render: (row) => <Mono>{row.ref}</Mono> },
  {
    key: "status",
    header: "Outcome",
    render: (row) => <Verdict status={row.verificationStatus} />,
  },
  {
    key: "severity",
    header: "Severity",
    render: (row) => (
      <ToneBadge tone={row.severity === "critical" || row.severity === "high" ? "fail" : "unknown"}>
        {row.severity}
      </ToneBadge>
    ),
  },
  {
    key: "opened",
    header: "Opened",
    render: (row) => <Mono>{formatRelativeTime(row.openedAt)}</Mono>,
  },
  {
    key: "action",
    header: "Action",
    render: (row) => (
      <ActionButton
        actionId={ANALYTICS_OPEN_DRILLDOWN_ACTION}
        data-action-id={ANALYTICS_OPEN_DRILLDOWN_ACTION}
        data-finding-id={row.id}
        onClick={() => onNavigate(href("findings", row.id))}
      >
        Drill down
      </ActionButton>
    ),
  },
];

export function OutcomesPage({ onNavigate }: VerificationPageProps) {
  const verified = outcomeRows.filter((row) => row.aggregate === "PASS").length;
  const regressed = outcomeRows.filter((row) => row.aggregate === "FAIL").length;
  const inconclusive = outcomeRows.filter((row) => row.aggregate === "UNKNOWN").length;

  return (
    <PageFrame
      page="outcomes"
      wide
      title="Outcomes"
      lede="Observed results after a run: verified, regressed, inconclusive, or still waiting on an independent source."
      actions={
        <ActionButton
          actionId={OUTCOMES_OBSERVE_ACTION}
          data-action-id={OUTCOMES_OBSERVE_ACTION}
          variant="primary"
          onClick={() => onNavigate(href("runs"))}
        >
          Observe outcome
        </ActionButton>
      }
    >
      <StatGrid cols={4}>
        <Stat label="Verified" value={verified} tone="pass" foot="Runs with PASS receipts" />
        <Stat
          label="Regressed"
          value={regressed}
          tone="fail"
          foot="Runs that opened follow-up findings"
        />
        <Stat
          label="Inconclusive"
          value={inconclusive}
          tone="unknown"
          foot="UNKNOWN is not a soft pass"
        />
        <Stat
          label="Evidence bound"
          value={ARTIFACTS.length}
          foot="Artifacts available for replay"
        />
      </StatGrid>
      <Callout tone="unknown" title="Outcome evidence must outlive the claim">
        A deployment acknowledgement is evidence, not proof of the outcome. This screen keeps the
        run result, receipt, and independent observation in one row.
      </Callout>
      <DataTable
        title="Outcome observations"
        subtitle="Receipt-backed run outcomes with evidence counts and replay targets."
        rows={outcomeRows}
        rowKey={(row) => row.id}
        columns={[
          {
            key: "run",
            header: "Run",
            cellClassName: "whitespace-normal",
            render: (row) => (
              <div className="flex flex-col gap-0.5">
                <span className="font-medium text-foreground">{row.label}</span>
                <Mono>{row.id}</Mono>
              </div>
            ),
          },
          {
            key: "outcome",
            header: "Outcome",
            render: (row) => <Verdict status={row.aggregate} />,
          },
          {
            key: "receipt",
            header: "Receipt",
            render: (row) =>
              row.receiptId ? (
                <Mono>{row.receiptId}</Mono>
              ) : (
                <ToneBadge tone="unknown">missing</ToneBadge>
              ),
          },
          {
            key: "facts",
            header: "Bound facts",
            render: (row) =>
              `${formatNumber(row.controlCount)} controls · ${formatNumber(row.evidenceCount)} evidence`,
          },
          {
            key: "completed",
            header: "Observed",
            render: (row) =>
              row.completedAt ? (
                <Mono>{formatRelativeTime(row.completedAt)}</Mono>
              ) : (
                <ToneBadge tone="running">{row.status}</ToneBadge>
              ),
          },
          {
            key: "action",
            header: "Action",
            render: (row) =>
              row.receiptId ? (
                <ActionButton
                  actionId={OUTCOMES_OPEN_RECEIPT_ACTION}
                  data-action-id={OUTCOMES_OPEN_RECEIPT_ACTION}
                  data-receipt-id={row.receiptId}
                  onClick={() => onNavigate(href("receipts", row.receiptId ?? ""))}
                >
                  Open receipt
                </ActionButton>
              ) : (
                <span className="text-muted-foreground">No receipt</span>
              ),
          },
        ]}
      />
    </PageFrame>
  );
}

export function AutonomyPage({ onNavigate }: VerificationPageProps) {
  const admitted = CANDIDATES.filter((row) => row.status === "admitted").length;
  const blocked = CANDIDATES.length - admitted;
  return (
    <PageFrame
      page="autonomy"
      wide
      title="Autonomy"
      lede="Workflows earn autonomy only when the actor, verifier, evidence, and consequence boundary are explicit."
      actions={
        <ActionButton
          actionId={AUTONOMY_SIMULATE_ACTION}
          data-action-id={AUTONOMY_SIMULATE_ACTION}
          variant="primary"
          disabled
          reason={simulateDisabledReason}
        >
          Simulate policy
        </ActionButton>
      }
    >
      <StatGrid cols={3}>
        <Stat
          label="Admitted workflows"
          value={admitted}
          tone="pass"
          foot="Independent oracle named"
        />
        <Stat label="Blocked or draft" value={blocked} tone="unknown" foot="No silent graduation" />
        <Stat
          label="Critical auto-pass"
          value={0}
          tone="pass"
          foot="Not available without external proof"
        />
      </StatGrid>
      <Section
        title="Autonomy boundary"
        note="Admission is shown beside the oracle that makes the workflow checkable."
      >
        <DataTable
          rows={CANDIDATES}
          rowKey={(row) => row.id}
          columns={autonomyColumns(onNavigate)}
        />
      </Section>
    </PageFrame>
  );
}

export function AnalyticsPage({ onNavigate }: VerificationPageProps) {
  const totalFindings = FINDINGS.length;
  const openFindings = FINDINGS.filter((row) => row.status === "open").length;
  const passControls = CONTROLS.filter((row) => row.status === "PASS").length;
  const exportableEvents = RUNS.length + FINDINGS.length + RECEIPTS.length;
  return (
    <PageFrame
      page="analytics"
      wide
      title="Analytics"
      lede="Permission-safe rollups for verification health, outcome drift, and reviewer load."
      actions={
        <ActionButton
          actionId={ANALYTICS_EXPORT_ACTION}
          data-action-id={ANALYTICS_EXPORT_ACTION}
          variant="primary"
          disabled
          reason={exportDisabledReason}
        >
          Export rollup
        </ActionButton>
      }
    >
      <StatGrid cols={4}>
        <Stat
          label="Controls passing"
          value={`${passControls}/${CONTROLS.length}`}
          tone={passControls === CONTROLS.length ? "pass" : "unknown"}
          foot="From the control ledger"
        />
        <Stat
          label="Open findings"
          value={openFindings}
          tone={openFindings > 0 ? "fail" : "pass"}
          foot={`${totalFindings} total finding rows`}
        />
        <Stat label="Receipts issued" value={RECEIPTS.length} foot="Replayable proof envelopes" />
        <Stat label="Export events" value={exportableEvents} foot="Restricted payloads omitted" />
      </StatGrid>
      <Section
        title="Scorecard metrics"
        note="The export carries aggregate metrics and drilldown ids, not raw restricted evidence."
      >
        <DataTable
          rows={METRICS}
          rowKey={(row) => row.id}
          columns={[
            {
              key: "metric",
              header: "Metric",
              cellClassName: "whitespace-normal",
              render: (row: Metric) => (
                <div className="flex flex-col gap-0.5">
                  <span className="font-medium text-foreground">{row.label}</span>
                  <span className="text-muted-foreground">{row.note}</span>
                </div>
              ),
            },
            {
              key: "value",
              header: "Value",
              render: (row: Metric) => <Mono>{row.value}</Mono>,
            },
            {
              key: "target",
              header: "Target",
              render: (row: Metric) => <Mono>{row.target}</Mono>,
            },
            {
              key: "status",
              header: "Status",
              render: (row: Metric) => (
                <ToneBadge tone={METRIC_TONE[row.status]}>{row.status}</ToneBadge>
              ),
            },
          ]}
        />
      </Section>
      <Section
        title="Finding drilldowns"
        note="Each row opens the source case while the rollup remains permission-safe."
      >
        <DataTable
          rows={FINDINGS.slice(0, 6)}
          rowKey={(row) => row.id}
          columns={findingColumns(onNavigate)}
        />
      </Section>
    </PageFrame>
  );
}
